import React, { useState } from 'react';
import { useFormik } from 'formik';
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import * as yup from 'yup';
import CustomFormLabel from 'src/components/theme-elements/CustomFormLabel';
import { loginUser, loginFailureMsg, loginSuccessMsg } from 'src/store/auth/UserAuthSlice.jsx';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomOutlinedInputWithValidation from 'src/components/theme-elements/CustomOutlinedInputWithValidation';
import { IconAt, IconKey } from '@tabler/icons';
import { apiData } from 'src/services/api-methods';
import API from 'src/APIUrls';
import { GetUser } from 'src/hooks/useAuthentication';

import Brand from 'src/assets/main-brand/vedanta-logo.png';

import axios from 'axios';

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long'),
});

const AuthLogin = ({ title, subtext, loading, setLoading }) => {
  const user = GetUser();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const lgMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const lgSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const token = useToken();

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        console.log(values);

        // Perform the login request using axios directly
        const response = await axios.post(API.Auth.AuthSignIn, values, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success) {
          // Adjust according to the response structure
          // localStorage.setItem('token', response.data.data.token);

          // Prepare payload for login
          const payLoad = {
            user: {
              id: response.data.data.ID,
              email: response.data.data.user_email,
              firstName: response.data.data.first_name,
              // Add other user data as needed
            },
            token: response.data.data.token,
            loginTime: Date.now(),
          };

          dispatch(loginUser(payLoad)); // Save user data and token in the state
          navigate('/masters/category/list'); // Redirect user after successful login
        } else {
          // Handle login failure scenario
          dispatch(loginFailureMsg({ message: 'Login Failed' }));
          setLoading(false);
        }
      } catch (error) {
        // Handle any errors during login
        console.error('Login failed:', error);
        setLoading(false);
        dispatch(loginFailureMsg({ message: 'Login Failed. Please Try Again Later.' }));
      }
    },
  });

  const handleForgotPasswordClick = () => {
    navigate('/auth/forgot-password');
  };

  return (
    <Stack>
      {title ? (
        <Typography
          fontWeight="700"
          variant={lgUp ? 'h3' : lgMd ? 'h4' : 'h6'}
          mb={1}
          textAlign={`center`}
          display={'flex'}
          justify={'center'}
          alignItems={`center`}
          style={{ justifyContent: 'center', gap: 10, color: '#fff' }}
        >
          {title}
          <img src={Brand} alt="Sastraprakasika LOGO" style={{ width: '50%', marginTop: '7px' }} />
        </Typography>
      ) : null}

      {/* <Typography
        variant="subtitle1"
        color="textSecondary"
        mb={1}
        style={{
          textAlign: 'center',
          color: '#ffea00',
          fontWeight: 'bold',
          fontSize: lgUp ? '1.3rem' : lgMd ? '20px' : lgSm ? '15px' : '10px',
        }}
      >
        {subtext}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        mb={1}
        style={{
          textAlign: 'center',
          color: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontWeight: 'bold',
          fontSize: lgUp ? '1rem' : lgMd ? '20px' : lgSm ? '15px' : '10px',
        }}
      >
        Powered by{': '}
        <img src={SpicWLogo} alt="SPIC_Logo" style={{ marginLeft: '10px', width: '15%' }} />
      </Typography> */}

      {/* <AuthSocialButtons title="Sign in with" /> */}
      <Box mt={3}>
        <Divider>
          <Typography
            component="span"
            color="textSecondary"
            variant="h6"
            fontWeight="400"
            position="relative"
            px={2}
            sx={{ color: '#fff' }}
          >
            Sign In To Your Account
          </Typography>
        </Divider>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Stack>
          <Box>
            <CustomFormLabel htmlFor="username" sx={{ color: '#fff', float: 'left' }}>
              Email Id
            </CustomFormLabel>
            <CustomOutlinedInputWithValidation
              startAdornment={
                <InputAdornment position="start">
                  <IconAt size="20" color="#fff" />
                </InputAdornment>
              }
              fullWidth
              sx={{
                color: '#fff',
                fontSize: '1rem',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: formik.touched.email && formik.errors.email ? '#ff1744' : '#fff', // Default border color
                  },
                  '&:hover fieldset': {
                    borderColor:
                      formik.touched.email && formik.errors.email ? '#ff1744' : '#3AC4E0', // Hover color
                  },
                  '&.Mui-focused fieldset': {
                    borderColor:
                      formik.touched.email && formik.errors.email ? '#ff1744' : '#3AC4E0', // Focus color
                  },
                },
              }}
              id="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email && formik.errors.email ? (
                  <span style={{ color: '#ff1744' }}>{formik.errors.email}</span> // Error message color
                ) : null
              }
            />
          </Box>
          <Box>
            <CustomFormLabel htmlFor="password" sx={{ color: '#fff', float: 'left' }}>
              Password
            </CustomFormLabel>
            <CustomOutlinedInputWithValidation
              fullWidth
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                formik.touched.password && formik.errors.password ? (
                  <span style={{ color: '#ff1744' }}>{formik.errors.password}</span> // Error message color
                ) : null
              }
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      formik.touched.password && formik.errors.password ? '#ff1744' : '#fff', // Default border color
                  },
                  '&:hover fieldset': {
                    borderColor:
                      formik.touched.password && formik.errors.password ? '#ff1744' : '#3AC4E0', // Hover color
                  },
                  '&.Mui-focused fieldset': {
                    borderColor:
                      formik.touched.password && formik.errors.password ? '#ff1744' : '#3AC4E0', // Focus color
                  },
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <IconKey size="20" color="#fff" />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end">
                    {showPassword ? (
                      <Visibility sx={{ color: '#fff' }} />
                    ) : (
                      <VisibilityOff sx={{ color: '#fff' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
          <Stack justifyContent="space-between" direction="row" alignItems="center" my={3}>
            <Typography
              component="span"
              fontWeight="500"
              sx={{
                textDecoration: 'none',
                // color: 'primary.main',
                cursor: 'pointer',
                color: '#fff',
              }}
              onClick={handleForgotPasswordClick}
            >
              Forgot Password ?
            </Typography>
          </Stack>
        </Stack>
        <Box width="100%" display={`flex`} justifyContent={`center`}>
          <Button
            // color="primary"
            variant="contained"
            size="large"
            // fullWidth
            sx={{
              margin: 'auto',
              px: 8,
              backgroundColor: '#C35214',
              '&:hover': {
                backgroundColor: '#fff',
                color: '#C35214',
                fontWeight: 'bold',
                boxShadow: '0 5px 8px orange',
              },
            }}
            type="submit" // Removed component={Link} to="/" from here
          >
            Sign In
          </Button>
        </Box>
      </form>
      {/* {subtitle} */}
    </Stack>
  );
};

export default AuthLogin;
